import { FightSportPayload } from "@bracket/idl/main/fight/model/enums/FightSportPt"
import { TournamentStatusPayload } from "@bracket/idl/main/tournament/model/TournamentPt"

/**
 * 대회 상세 UI 모델.
 *
 * 서버는 대회 조회를 `POST /tournament/get-by-id` 하나로 내려주므로 화면 모델도 하나만 둔다
 * (예전에는 REST 용 모델이 따로 있었다 — 경로가 두 벌이 되면 화면마다 다른 값을 보게 된다).
 */
interface TournamentDetail {
  id: string
  name: string
  /** yyyy.MM.dd (하루짜리) 또는 yyyy.MM.dd ~ yyyy.MM.dd */
  periodText: string
  venue: string
  /** yyyy.MM.dd */
  startText: string
  /** yyyy.MM.dd HH:mm */
  entryOpenText: string
  /** yyyy.MM.dd HH:mm */
  entryCloseText: string
  /** 개최 종목 — 참가 신청 폼의 종목 선택 목록이 된다 */
  sports: FightSportPayload[]
  status: TournamentStatusPayload
  statusLabel: string
  isEntryOpen: boolean
  /** 안내사항 — HTML 이 올 수 있어 화면은 sanitizeHtml 을 통과시킨다 */
  notice: string
}

/** 대회 목록 한 줄 UI 모델 */
interface TournamentListItem {
  id: string
  name: string
  venue: string
  periodText: string
  startText: string
  entryCloseText: string
  status: TournamentStatusPayload
  statusLabel: string
  isEntryOpen: boolean
  sports: FightSportPayload[]
  /** 신청 건수 — 관리자 목록에서만 채워진다 */
  entryCount: number
}

/** 관리자 대회 등록 · 수정 폼 입력값 (`<input>` 가 다루므로 전부 문자열) */
interface TournamentFormInput {
  id: string
  name: string
  venue: string
  /** yyyy-MM-ddTHH:mm (datetime-local) */
  startAt: string
  endAt: string
  entryOpenAt: string
  entryCloseAt: string
  status: string
  notice: string
  /** 개최 종목 — IDL enum 이름 문자열 */
  sports: string[]
}

type TournamentFormErrors = Partial<Record<keyof TournamentFormInput, string>>

const TOURNAMENT_STATUS_LABELS: Record<TournamentStatusPayload, string> = {
  [TournamentStatusPayload.TOURNAMENT_STATUS_UNKNOWN]: "알 수 없음",
  [TournamentStatusPayload.TOURNAMENT_SCHEDULED]: "접수 예정",
  [TournamentStatusPayload.TOURNAMENT_ENTRY_OPEN]: "접수 중",
  [TournamentStatusPayload.TOURNAMENT_ENTRY_CLOSED]: "접수 마감",
  [TournamentStatusPayload.TOURNAMENT_ONGOING]: "진행 중",
  [TournamentStatusPayload.TOURNAMENT_FINISHED]: "종료",
  [TournamentStatusPayload.TOURNAMENT_CANCELED]: "취소",
  [TournamentStatusPayload.UNRECOGNIZED]: "알 수 없음",
}

/** 관리자 상태 선택지 — 삭제는 행을 지우는 별도 버튼이라 「취소」는 새로 고르지 않는다 */
const TOURNAMENT_STATUS_OPTIONS = [
  { value: "TOURNAMENT_SCHEDULED", label: "접수 예정" },
  { value: "TOURNAMENT_ENTRY_OPEN", label: "접수 중" },
  { value: "TOURNAMENT_ENTRY_CLOSED", label: "접수 마감" },
  { value: "TOURNAMENT_ONGOING", label: "진행 중" },
  { value: "TOURNAMENT_FINISHED", label: "종료" },
]

function tournamentStatusLabel(status: TournamentStatusPayload): string {
  return TOURNAMENT_STATUS_LABELS[status] ?? "알 수 없음"
}

export {
  TOURNAMENT_STATUS_LABELS,
  TOURNAMENT_STATUS_OPTIONS,
  tournamentStatusLabel,
}
export type {
  TournamentDetail,
  TournamentFormErrors,
  TournamentFormInput,
  TournamentListItem,
}
